/**
 * PageHeader — breadcrumb + title + subtitle + action buttons
 *
 * @example
 * <PageHeader
 *   breadcrumbs={[
 *     { label: t('admin.nav_dashboard'), href: '/admin' },
 *     { label: t('admin.nav_users') },
 *   ]}
 *   title={t('admin.nav_users')}
 *   subtitle={t('admin.users_subtitle')}
 *   actions={
 *     <Button onClick={() => setOpen(true)}>
 *       <Plus size={16} /> {t('admin.btn_add_user')}
 *     </Button>
 *   }
 * />
 */
import React from 'react';
import PropTypes from 'prop-types';
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

export default function PageHeader({ breadcrumbs, title, subtitle, actions, className }) {
  return (
    <div className={cn('mb-6', className)}>
      {/* Breadcrumb */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav
          aria-label="breadcrumb"
          className="mb-2 flex items-center gap-1 text-xs text-[var(--text-muted)] font-semibold"
        >
          {breadcrumbs.map((crumb, i) => (
            <React.Fragment key={i}>
              {i > 0 && <ChevronRight size={12} className="opacity-40" aria-hidden />}
              {crumb.href ? (
                <Link
                  to={crumb.href}
                  onClick={crumb.onClick}
                  className="hover:text-[var(--brand-primary)] transition-colors"
                >
                  {crumb.label}
                </Link>
              ) : (
                <span className="text-[var(--text-main)]" aria-current="page">
                  {crumb.label}
                </span>
              )}
            </React.Fragment>
          ))}
        </nav>
      )}

      {/* Title + Actions */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-[var(--text-main)] leading-tight">
            {title}
          </h1>
          {subtitle && (
            <p className="text-sm text-[var(--text-muted)] mt-0.5">{subtitle}</p>
          )}
        </div>
        {actions && (
          <div className="flex flex-wrap items-center gap-2">{actions}</div>
        )}
      </div>
    </div>
  );
}

PageHeader.propTypes = {
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      href: PropTypes.string,
      // Opsiyonel — kaydedilmemiş değişiklik korumalı sayfalar (örn.
      // AdminGeoContentDetail düzenleme modu) `e.preventDefault()` ile
      // navigasyonu durdurup onay diyaloğu açabilir. Sağlanmazsa Link
      // normal davranışıyla (doğrudan navigasyon) çalışır — geriye dönük
      // uyumlu, diğer sayfalarda davranış değişmez.
      onClick: PropTypes.func,
    }),
  ),
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  actions: PropTypes.node,
  className: PropTypes.string,
};
